import { Modal, roundNumber, SubmitButton } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import { FormField, handleSuccessfulSubmission, InputField, isOtherSellerPurchase, requiredNumber } from "common";
import { Field, FieldProps, FormikProps } from "formik";
import { produce } from "immer";
import { noop } from "lodash";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import {
    Event,
    LineItemObject,
    LineItemType,
    Payment,
    PaymentLineItemStatus,
    PaymentMethod,
    Purchase,
    PurchaseItemObject,
} from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { getFormValuesForAuthorizeNet, handlePaymentError } from "../../helpers/authNetPaymentHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useStripeTerminal } from "../../hooks/useStripeTerminal";
import {
    getInitialPaymentMethod,
    getPendingCreditCardLineItem,
    needsPrepare,
    shouldEnableDrawerEject,
} from "../../models/Purchase";
import { invalidateSellerBalance, isAuthorizeNetEnabled } from "../../models/Seller";
import { getAutoPrint } from "../../storages/PrinterStorage";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { Printer } from "../Common/Printer";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { updatePurchaseTimeslotCache } from "../Event/EditEventCapacityModal";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { PaymentInput } from "../Input/PaymentInput";
import { PaymentInputRef, TokenizePaymentParams } from "../Input/PaymentInputRef";
import { CollectBalanceFormValues } from "./CollectPurchaseBalanceTypes";
import { PaymentBreakdown } from "./PaymentBreakdown";
import { useAuthorization } from "@/hooks/useAuthorization";

interface CollectPurchaseBalanceModalProps {
    purchase: Purchase;
    event?: Event;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (id: string) => void;
}

const validationSchema = object({
    amount: requiredNumber()
        .test({
            name: "checkAmount",
            message: () => {
                return "Amount must be 0.5 at minimum";
            },
            test(value) {
                const paymentMethod = this.parent.payment.method;

                if (paymentMethod === PaymentMethod.CREDIT_CARD) {
                    return Number(value) >= 0.5;
                }

                return true;
            },
        })
        .test({
            name: "checkNonCreditCardAmount",
            message: "Amount must be 0.01 at minimum",
            test(value) {
                const paymentMethod = this.parent.payment.method;

                if (paymentMethod !== PaymentMethod.CREDIT_CARD) {
                    return Number(value) >= 0.01;
                }

                return true;
            },
        }),
});

export const CollectPurchaseBalanceModal = ({
    purchase,
    event,
    isOpen,
    onClose,
    onSuccess,
}: CollectPurchaseBalanceModalProps) => {
    /**
     * Using state here instead of useMutation isLoading,
     * since we need to have it truthy until invalidation is done,
     * and not when just PATCH /purchase is done
     */
    const [isLoading, setIsLoading] = useState(false);
    const handleSubmit = async (values: CollectBalanceFormValues) => {
        setIsLoading(true);
        const result = await collectBalance(purchase, values);
        onSuccess?.(purchase.id);
        return result;
    };

    const handleClose = () => {
        onClose();
        setIsLoading(false);
    };

    return (
        <Modal size="huge" isOpen={isOpen} className="max-w-[900px]" onClose={isLoading ? noop : onClose}>
            <CollectBalanceModalContent
                purchase={purchase}
                event={event}
                onFetchRequestData={getPayload}
                onSubmit={handleSubmit}
                onClose={handleClose}
            />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    event?: Event;
    isAddFundModal?: boolean;
    onFetchRequestData: (purchase: Purchase, values: CollectBalanceFormValues) => void;
    onClose: () => void;
    onSubmit: (values: CollectBalanceFormValues) => Promise<AxiosResponse<Purchase>>;
}

export const CollectBalanceModalContent = ({
    purchase,
    event,
    isAddFundModal,
    onFetchRequestData,
    onClose,
    onSubmit,
}: ModalContentProps) => {
    useStripeTerminal();
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const paymentInputRef = useRef<PaymentInputRef>(null);
    const isEMV = useCreatePurchaseStore((state) => state.isEMV);
    const cardInputMethod = useCreatePurchaseStore((state) => state.cardInputMethod);
    const collectBalanceFormRef = useRef<FormikProps<CollectBalanceFormValues>>(null);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [isUpdatingPurchase, setIsUpdatingPurchase] = useState(false);
    const [enableDrawerEject, setEnableDrawerEject] = useState<boolean>(false);

    const isMistHasGiftAndExperience =
        purchase.items.some((item) => item.object === PurchaseItemObject.EXPERIENCE) &&
        purchase.items.some((item) => item.object === PurchaseItemObject.GIFT);

    const showAsLater =
        purchase.items.some(
            (item) => item.object !== PurchaseItemObject.GIFT && item.object !== PurchaseItemObject.STORE_CREDIT,
        ) && !isMistHasGiftAndExperience;

    const getFormValues = async (values: CollectBalanceFormValues, tokenizePaymentParams?: TokenizePaymentParams) => {
        if (values.payment.method === PaymentMethod.CREDIT_CARD && isAuthorizeNetEnabled(seller)) {
            return getFormValuesForAuthorizeNet(values, paymentInputRef, tokenizePaymentParams, cardInputMethod);
        }

        return values;
    };

    const {
        mutateAsync: collectPurchaseBalance,
        isLoading: isSaving,
        isSuccess,
        reset,
    } = useMutation(async (values: CollectBalanceFormValues) => {
        try {
            let formValues = (await getFormValues(values)) as CollectBalanceFormValues;
            const isCashPayment = formValues.payment.method === PaymentMethod.CASH;

            // if payment method is not equal to cash then it will return
            if (needsPrepare() || isCashPayment) {
                const response = await paymentInputRef.current?.prepare?.();
                if (!response) {
                    return;
                }

                if (formValues.payment.method === PaymentMethod.CASH) {
                    formValues = produce(formValues, (draft) => {
                        draft.payment = response as Payment;
                    });
                }
            }

            const { data: updatedPurchase } = await onSubmit(formValues);

            const creditCardPaymentLineItem = getPendingCreditCardLineItem(updatedPurchase);

            const clientSecret = creditCardPaymentLineItem?.payment?.paymentIntent?.clientSecret;
            const inputResponse = await paymentInputRef.current?.confirm?.(clientSecret ? { clientSecret } : {});
            const paymentIntentMethods = creditCardPaymentLineItem?.payment?.paymentIntentMethods;

            const isCCPayment = inputResponse && clientSecret;
            const isEMVPayment = isEMV && paymentIntentMethods?.includes("card_present");

            if (isCCPayment || (isEMVPayment && formValues.payment.method === PaymentMethod.CREDIT_CARD)) {
                const url = `/purchases/${purchase.id}/lineItems/${creditCardPaymentLineItem?.id}`;
                await api.patch<Purchase>(url, { status: PaymentLineItemStatus.PROCESSING });
            }

            if (getAutoPrint()) {
                const drawerEjectPreference = shouldEnableDrawerEject(updatedPurchase);
                setEnableDrawerEject(drawerEjectPreference);
            }

            handlePaymentSuccess(formValues.payment.method === PaymentMethod.CREDIT_CARD);
        } catch (error: any) {
            reset(); // reset success status in case of exception
            if (error) {
                // This is required to open additional billing details modal in case there is an error while making authorize.net payment
                handlePaymentError(error, seller, paymentInputRef);
            }
        }
    });

    const handleSubmit = async (values: CollectBalanceFormValues) => {
        await collectPurchaseBalance(values);
    };

    const handleAdditionalBillingSubmit = async (
        tokenizePaymentParams: TokenizePaymentParams,
        values: CollectBalanceFormValues,
    ) => {
        try {
            const formValues = (await getFormValues(values, tokenizePaymentParams)) as CollectBalanceFormValues;
            await onSubmit(formValues);
            handlePaymentSuccess();
        } catch (error: any) {
            paymentInputRef.current?.onPaymentError?.(error.response.data.global);
        }
    };

    const handlePaymentSuccess = (isCCPayment?: boolean) => {
        handleSuccessfulSubmission(() => {
            if (!event) {
                invalidate("purchases", purchase.id);
            }

            if (isCCPayment) {
                invalidateSellerBalance();
            }

            if (!isOtherSellerPurchase(purchase, seller.id)) {
                invalidate("users", purchase.organizer?.id);
            }

            updatePurchaseTimeslotCache(purchase, isGranted);

            setTimeout(() => {
                updatePurchaseTimeslotCache(purchase, isGranted);
                onClose();
            }, 1000); // give some time for elastic search to get updated and invalidate to get new data
        });
    };

    let initialValues: CollectBalanceFormValues = {
        amount: isAddFundModal ? 0 : purchase.balance,
        payAmount: purchase.balance,
        tag: "",
        notify: true,
        payment: { method: getInitialPaymentMethod(seller, purchase).id as PaymentMethod },
    };

    if (collectBalanceFormRef.current?.dirty && !isFormDirty) {
        setIsFormDirty(true);
    }

    if (isFormDirty) {
        initialValues = {
            ...initialValues,
            ...collectBalanceFormRef.current?.values,
        };
    }

    if (isEMV) {
        delete initialValues.payment.card;
        initialValues.payment = {
            ...initialValues.payment,
            paymentIntentMethods: ["card_present"],
        };
    } else if (initialValues.payment.paymentIntentMethods) {
        delete initialValues.payment.paymentIntentMethods;
    }

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            innerRef={collectBalanceFormRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isValid, values, setFieldValue }) => {
                const handlePurchaseUpdate = (purchase: Purchase | null) => {
                    if (purchase) {
                        setIsFormDirty(true);
                        setFieldValue("payAmount", purchase.balance);
                    }
                };

                const isPaymentInputRefHasError =
                    paymentInputRef?.current?.getError && paymentInputRef?.current?.getError();

                return (
                    <>
                        <Modal.Header>Collect Payment</Modal.Header>

                        <Modal.Body className="flex justify-between space-x-4">
                            <fieldset className="max-w-100 flex-auto shrink-0 space-y-4" disabled={isSaving}>
                                <FormField name="amount" label="Amount">
                                    <CurrencyInputField isRequired placeholder="Amount" />
                                </FormField>

                                <Field name="payment">
                                    {({ form, meta }: FieldProps) => (
                                        <PaymentInput
                                            ref={paymentInputRef}
                                            value={meta.value}
                                            amount={initialValues.amount}
                                            organizer={purchase?.organizer?.id}
                                            excludePaymentMethods={[PaymentMethod.INVOICE]}
                                            showDoNotChargeCardSwitch={false}
                                            isPartnerBooking={isOtherSellerPurchase(purchase, seller.id)}
                                            onChange={(value) => form.setFieldValue("payment", value)}
                                            onError={(error) => form.setFieldError("payment", error ?? undefined)}
                                            onAdditionalDetailsSubmit={async (data) =>
                                                handleAdditionalBillingSubmit(data, values)
                                            }
                                        />
                                    )}
                                </Field>

                                {!isOtherSellerPurchase(purchase, seller.id) && (
                                    <FormField name="tag" label="Tag">
                                        <InputField placeholder="Add a tag" />
                                    </FormField>
                                )}
                            </fieldset>
                            <div className="grow rounded-lg bg-gray-lighter p-6">
                                <PaymentBreakdown
                                    purchase={purchase}
                                    payload={onFetchRequestData(purchase, values)}
                                    isUpdating={setIsUpdatingPurchase}
                                    isEnabled={!isSuccess}
                                    onUpdate={handlePurchaseUpdate}
                                />
                            </div>

                            <Printer
                                enablePaperCut
                                enableDrawerEject={enableDrawerEject}
                                shouldStart={enableDrawerEject}
                            />
                        </Modal.Body>

                        <Modal.Footer className="flex justify-end space-x-4">
                            <fieldset disabled={isSaving} className="flex space-x-4">
                                <Field name="notify">
                                    {({ field, form }: FieldProps) => (
                                        <NotifyCustomerCheckbox
                                            isChecked={field.value}
                                            onChange={(e) => form.setFieldValue("notify", e.target.checked)}
                                        />
                                    )}
                                </Field>

                                <SecondaryButton onClick={onClose}>{showAsLater ? "Later" : "Cancel"}</SecondaryButton>

                                <SubmitButton
                                    isLoading={isSaving}
                                    disabled={!isValid || isUpdatingPurchase || isPaymentInputRefHasError}
                                    isSuccess={isSuccess}
                                    onClick={handleSubmit}
                                >
                                    Collect
                                </SubmitButton>
                            </fieldset>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

const collectBalance = async (purchase: Purchase, values: CollectBalanceFormValues) => {
    return api.patch<Purchase>(`/purchases/${purchase.id}`, getPayload(purchase, values));
};

const getPayload = (purchase: Purchase, values: CollectBalanceFormValues) => {
    const coupon = purchase.lineItems.find((item: any) => item.couponCode);
    const paymentMethodsWithCode = [PaymentMethod.GIFT, PaymentMethod.STORE_CREDIT, PaymentMethod.VOUCHER];
    const requiresCode =
        values.payment.method && paymentMethodsWithCode.includes(values.payment.method as PaymentMethod);
    return {
        ops: [
            {
                path: "/lineItems",
                op: "addLineItem",
                data: {
                    amount: -1 * roundNumber(purchase.currency, values.amount),
                    name: "Payment", // Send a default name if empty
                    object: LineItemObject.PAYMENT,
                    type: LineItemType.PAYMENT,
                    payment: { ...values.payment, comment: values.tag },
                    ...(requiresCode && { code: coupon?.couponCode?.code ?? null }),
                },
            },
        ],
        _emailOverrides: { sendEmailToTraveler: values.notify },
    };
};
